import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  DialogContentText,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { ContentCopy as CopyIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { leadsAPI } from '../../services/api';

export default function EnrollmentLinkDialog({
  open,
  onClose,
  lead,
  onSuccess,
  loading = false,
}) {
  const [generating, setGenerating] = useState(false);
  const [enrollmentUrl, setEnrollmentUrl] = useState('');
  const [error, setError] = useState('');

  const handleClose = () => {
    if (!generating) {
      const hadUrl = !!enrollmentUrl;
      setEnrollmentUrl('');
      setError('');
      onClose();
      if (hadUrl && onSuccess) onSuccess();
    }
  };

  const handleGenerate = async () => {
    if (!lead?.id) {
      setError('Lead ID is missing. Close and reopen from the leads list.');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const res = await leadsAPI.generateEnrollmentLink(lead.id, {});
      const url = res.data?.enrollmentUrl || '';
      if (!url) {
        setError('Server did not return an enrollment link. Check ENROLLMENT_BASE_URL in backend .env.');
        return;
      }
      setEnrollmentUrl(url);
      if (res.data?.reused) {
        toast.success('Existing enrollment link retrieved. Share it with the parent.');
      } else {
        toast.success('Enrollment link generated. Lead is now Enrollment Pending.');
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Failed to generate enrollment link';
      setError(msg);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!enrollmentUrl) return;
    try {
      await navigator.clipboard.writeText(enrollmentUrl);
      toast.success('Link copied to clipboard');
    } catch {
      toast.error('Could not copy link');
    }
  };

  const leadName = lead ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim() : '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Generate enrollment link</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        <DialogContentText sx={{ mb: 2 }}>
          Generate an enrollment form link for {leadName || 'this lead'}? The lead will be set to
          &quot;Enrollment Pending&quot;. Share the link with the parent so they can fill the form.
          After submission, they will become Won and a student.
        </DialogContentText>

        {enrollmentUrl && (
          <Box sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Enrollment link"
              value={enrollmentUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopy} edge="end" title="Copy link">
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={generating || loading}>
          {enrollmentUrl ? 'Close' : 'Cancel'}
        </Button>
        {!enrollmentUrl && (
          <Button
            onClick={handleGenerate}
            variant="contained"
            color="primary"
            disabled={generating || loading}
          >
            {generating ? <CircularProgress size={20} /> : 'Generate link'}
          </Button>
        )}
        {enrollmentUrl && (
          <Button onClick={handleCopy} variant="contained" color="primary">
            Copy link
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
